import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Check, X, Upload, ImageIcon } from 'lucide-react'
import { uploadImageToStorageSecure } from '@/lib/secureFileUpload'

interface TattooImageGalleryProps {
  postUrls: string[]
  selectedTattooUrls: string[]
  onSelectionChange: (selectedUrls: string[]) => void
  onboardingId?: string // Add onboarding ID for upload path
}

export function TattooImageGallery({ 
  postUrls, 
  selectedTattooUrls, 
  onSelectionChange,
  onboardingId
}: TattooImageGalleryProps) {
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<string>>(new Set())
  const [uploadedImages, setUploadedImages] = useState<string[]>([])
  const [uploadingImages, setUploadingImages] = useState<Set<string>>(new Set())

  const handleImageError = (url: string) => {
    setImageLoadErrors(prev => new Set(prev).add(url))
  }

  const toggleImageSelection = (url: string) => {
    const isSelected = selectedTattooUrls.includes(url)
    
    if (isSelected) {
      // Remove from selection
      onSelectionChange(selectedTattooUrls.filter(selectedUrl => selectedUrl !== url))
    } else {
      // Add to selection
      onSelectionChange([...selectedTattooUrls, url])
    }
  }

  const selectAll = () => {
    const validUrls = postUrls.filter(url => !imageLoadErrors.has(url))
    onSelectionChange(validUrls)
  }

  const deselectAll = () => {
    onSelectionChange([])
  }

  const handleBatchUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    
    if (!onboardingId) {
      console.error('Cannot upload images: onboardingId is required')
      return
    }
    
    for (const file of files) {
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      
      try {
        // Start uploading indicator
        setUploadingImages(prev => new Set([...prev, tempId]))
        
        // Upload to Firebase Storage in the onboardings folder
        const firebaseUrl = await uploadImageToStorageSecure(
          file,
          `onboardings/${onboardingId}/admin-uploads`,
          onboardingId // Use onboardingId as userId for admin uploads
        )
        
        // Add the Firebase URL to uploaded images and selection
        setUploadedImages(prev => [...prev, firebaseUrl])
        onSelectionChange([...selectedTattooUrls, firebaseUrl])
        
      } catch (error) {
        console.error('Failed to upload portfolio image:', error)
        // TODO: Show error toast to user
      } finally {
        setUploadingImages(prev => {
          const next = new Set(prev)
          next.delete(tempId)
          return next
        })
      }
    }
    
    // Reset the input so the same files can be uploaded again if needed
    event.target.value = ''
  }

  const removeUploadedImage = (urlToRemove: string) => {
    setUploadedImages(prev => prev.filter(url => url !== urlToRemove))
    // Also remove from selection
    onSelectionChange(selectedTattooUrls.filter(url => url !== urlToRemove))
  }

  const validImageUrls = postUrls.filter(url => !imageLoadErrors.has(url))
  const selectedCount = selectedTattooUrls.length
  const totalCount = validImageUrls.length
  const instagramSelectedCount = selectedTattooUrls.filter(url => postUrls.includes(url)).length
  const uploadedSelectedCount = selectedTattooUrls.filter(url => uploadedImages.includes(url)).length

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Portfolio Images</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              {selectedCount} total selected
            </Badge>
            <Badge variant="outline">
              {instagramSelectedCount} from Instagram
            </Badge>
            <Badge variant="outline">
              {uploadedSelectedCount} uploaded
            </Badge>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={selectAll}
            disabled={selectedCount === totalCount}
          >
            Select All Instagram
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={deselectAll}
            disabled={selectedCount === 0}
          >
            Deselect All
          </Button>
          <div className="flex-1"></div>
          <div>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleBatchUpload}
              className="hidden"
              id="batch-upload-input"
            />
            <Button
              variant="outline"
              size="sm"
              asChild
              className="cursor-pointer"
              disabled={uploadingImages.size > 0}
            >
              <label htmlFor="batch-upload-input" className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                {uploadingImages.size > 0 ? `Uploading ${uploadingImages.size}...` : 'Upload Images'}
              </label>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Instagram Images Section */}
          {postUrls.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold">Instagram Posts</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {postUrls.map((url, index) => {
              const isSelected = selectedTattooUrls.includes(url)
              const hasError = imageLoadErrors.has(url)
              
              if (hasError) {
                return (
                  <div
                    key={index}
                    className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-sm"
                  >
                    Failed to load
                    </div>
                  )
                }

                return (
                <div
                  key={index}
                  className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                    isSelected 
                      ? 'border-primary shadow-lg' 
                      : 'border-transparent hover:border-gray-300'
                  }`}
                  onClick={() => toggleImageSelection(url)}
                >
                  <img
                    src={url}
                    alt={`Instagram post ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={() => handleImageError(url)}
                    loading="lazy"
                  />
                  
                  {/* Selection overlay */}
                  <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${
                    isSelected ? 'bg-black/30' : 'bg-black/0 hover:bg-black/10'
                  }`}>
                    {isSelected && (
                      <div className="bg-primary text-primary-foreground rounded-full p-2">
                        <Check className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                  
                  {/* Selection indicator in corner */}
                  <div className={`absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    isSelected 
                      ? 'bg-primary border-primary text-primary-foreground' 
                      : 'bg-white border-gray-300'
                  }`}>
                    {isSelected && <Check className="w-3 h-3" />}
                  </div>
                  </div>
                )
              })}
              </div>
            </div>
          )}

          {/* Uploaded Images Section */}
          {uploadedImages.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold">Uploaded Images</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {uploadedImages.map((url, index) => {
                  const isSelected = selectedTattooUrls.includes(url)
                  
                  return (
                    <div
                      key={`uploaded-${index}`}
                      className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                        isSelected 
                          ? 'border-primary shadow-lg' 
                          : 'border-transparent hover:border-gray-300'
                      }`}
                      onClick={() => toggleImageSelection(url)}
                    >
                      <img
                        src={url}
                        alt={`Uploaded image ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      
                      {/* Selection overlay */}
                      <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${
                        isSelected ? 'bg-black/30' : 'bg-black/0 hover:bg-black/10'
                      }`}>
                        {isSelected && (
                          <div className="bg-primary text-primary-foreground rounded-full p-2">
                            <Check className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                      
                      {/* Selection indicator in corner */}
                      <div className={`absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        isSelected 
                          ? 'bg-primary border-primary text-primary-foreground' 
                          : 'bg-white border-gray-300'
                      }`}>
                        {isSelected && <Check className="w-3 h-3" />}
                      </div>

                      {/* Remove button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          removeUploadedImage(url)
                        }}
                        className="absolute top-2 left-2 bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-destructive/90 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>

                      {/* Upload badge */}
                      <div className="absolute bottom-2 left-2">
                        <Badge variant="secondary" className="text-xs">
                          Uploaded
                        </Badge>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Upload Area (when no images uploaded yet) */}
          {uploadedImages.length === 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold">Upload Additional Images</h3>
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                    <ImageIcon className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium">No additional images uploaded</p>
                    <p className="text-sm text-muted-foreground">
                      Use the "Upload Images" button above to add more portfolio images
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Empty state */}
          {postUrls.length === 0 && uploadedImages.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium">No images found</p>
                  <p className="text-sm">Upload images to start building the portfolio</p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {selectedCount > 0 && (
          <div className="mt-6 p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">
              Selected tattoo images ({selectedCount}):
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedTattooUrls.map((url, index) => (
                <div
                  key={index}
                  className="relative group"
                >
                  <img
                    src={url}
                    alt={`Selected tattoo ${index + 1}`}
                    className="w-12 h-12 object-cover rounded border"
                  />
                  <button
                    onClick={() => toggleImageSelection(url)}
                    className="absolute -top-1 -right-1 bg-destructive text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-2 h-2" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
